import { useState, useEffect } from 'react';
import { Autocomplete, Avatar, Box, TextField, CircularProgress, Typography } from '@mui/material';
import { supabase } from '../../providers/supabase/supabase';

interface ArtistOption {
    id: string;
    name: string;
    imageUrl: string | null;
}

interface ArtistSearchProps {
    onSelect: (artistId: string | null, imageUrl?: string | null) => void;
}

export const ArtistSearch = ({ onSelect }: ArtistSearchProps) => {
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<ArtistOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState<ArtistOption | null>(null);

    useEffect(() => {
        if (inputValue.length < 2) {
            setOptions([]);
            return;
        }

        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const { data, error } = await supabase
                    .from('artists')
                    .select('id, name, image_url')
                    .ilike('name', `%${inputValue}%`)
                    .limit(20);

                if (!error && data) {
                    setOptions(
                        data.map((a: any) => ({
                            id: String(a.id),
                            name: a.name || `Artist ${a.id}`,
                            imageUrl: a.image_url || null,
                        }))
                    );
                }
            } catch {
                // Silently fail on search errors
            } finally {
                setLoading(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [inputValue]);

    return (
        <Box>
            <Autocomplete
                options={options}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={loading}
                onInputChange={(_, value) => setInputValue(value)}
                onChange={(_, value) => {
                    setSelected(value);
                    onSelect(value?.id ?? null, value?.imageUrl);
                }}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        <Avatar
                            src={option.imageUrl || undefined}
                            sx={{ width: 28, height: 28, mr: 1.5 }}
                        >
                            {option.name[0]}
                        </Avatar>
                        {option.name}
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Search Artist"
                        placeholder="Type artist name..."
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading && <CircularProgress size={20} />}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                noOptionsText={inputValue.length < 2 ? 'Type at least 2 characters' : 'No artists found'}
                fullWidth
            />
            {selected && (
                <Box display="flex" alignItems="center" gap={1.5} mt={1.5}>
                    <Avatar
                        src={selected.imageUrl || undefined}
                        sx={{ width: 48, height: 48 }}
                    >
                        {selected.name[0]}
                    </Avatar>
                    <Typography variant="body2" fontWeight={500}>
                        {selected.name}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
